import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { articles, getArticlesByCategory } from "@/mocks/articles";
import { buildBreadcrumbSchema } from "@/lib/seo";
import { tintMap } from "@/lib/content";
import { SITE_URL } from "@/mocks/site";

export default function Topic() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);

  if (!category) {
    return (
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-3xl text-foreground-950">分类不存在</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的分类可能已被调整，可以回到分类总览重新选择。</p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>返回主题分类
          </Link>
        </div>
      </section>
    );
  }

  const tint = tintMap[category.tint];
  const list = getArticlesByCategory(category.slug);

  const jsonLd = [
    buildBreadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "主题分类", path: "/topics" },
      { name: category.name, path: `/topic/${category.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.name,
      url: `${SITE_URL}/topic/${category.slug}`,
      description: category.desc,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
  ];

  return (
    <>
      <Seo
        title={`${category.name} - 黑料看点${category.shortName}每日精选看点`}
        description={`${category.desc} 黑料看点${category.name}栏目持续更新，为你整理该方向值得关注的看点与解读。`}
        keywords={`黑料看点${category.shortName},${category.name},${category.shortName}看点精选`}
        path={`/topic/${category.slug}`}
        jsonLd={jsonLd}
      />

      <section className="bg-background-50 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 pt-6 pb-8 md:pt-8 md:pb-10">
          <Breadcrumbs items={[{ name: "主题分类", path: "/topics" }, { name: category.name }]} />
          <div className="mt-6 flex items-start gap-4">
            <span className={`w-14 h-14 flex items-center justify-center rounded-md ${tint.chipBg} ${tint.chipText} shrink-0`}>
              <i className={`${category.icon} text-3xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">{category.name}</h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">{category.desc}</p>
              <p className="mt-3 text-xs text-foreground-500">共收录 {list.length} 篇看点 · 持续更新</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-8">
              {list.length > 0 ? (
                <div className="flex flex-col gap-8 md:gap-9">
                  {list.map((item) => (
                    <div key={item.id} className="pb-8 md:pb-9 border-b border-background-200 last:border-b-0 last:pb-0">
                      <ArticleCard article={item} variant="wide" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-600">该分类内容正在补充中，欢迎稍后再来。</p>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-950 pb-4 border-b border-background-200">
                  其他分类
                </h2>
                <ul className="mt-3 space-y-1">
                  {categories
                    .filter((item) => item.slug !== category.slug)
                    .map((item) => (
                      <li key={item.slug}>
                        <Link
                          to={`/topic/${item.slug}`}
                          className="flex items-center justify-between py-2 text-sm text-foreground-700 hover:text-primary-700 transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <i className={`${item.icon} text-foreground-500`}></i>
                            {item.name}
                          </span>
                          <i className="ri-arrow-right-s-line text-foreground-400"></i>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-950 pb-3 border-b border-background-200">
                  全站热读
                </h2>
                <div className="mt-1">
                  {[...articles]
                    .sort((a, b) => b.views - a.views)
                    .slice(0, 5)
                    .map((item, index) => (
                      <ArticleCard key={item.id} article={item} variant="minimal" rank={index + 1} />
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}